import React, { useState, useRef, useEffect, useCallback } from "react";
import axiosInstance from "../utils/axiosInstance";

const MAX_CHARS = 2000;

const OherezaIkibazo = ({ onSuccess }) => {
  const [form, setForm]       = useState({ title: "", ikibazo: "" });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState(null);
  const textareaRef           = useRef(null);

  /* ── auto-expand textarea ── */
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => { autoResize(); }, [form.ikibazo, autoResize]);

  const notify = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())                      e.title   = "Umutwe w'ikibazo ni ngombwa";
    if (!form.ikibazo.trim())                    e.ikibazo = "Ikibazo ni ngombwa";
    else if (form.ikibazo.trim().length < 10)    e.ikibazo = "Ikibazo kigomba kuba nibura inyuguti 10";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axiosInstance.post("/api/questions", { question_text: form.ikibazo });
      notify("✅ Ikibazo cyoherejwe neza kuri Akagari!");
      setForm({ title: "", ikibazo: "" });
      setErrors({});
      if (onSuccess) onSuccess();
    } catch (err) {
      notify(err.response?.data?.error || "Habaye ikosa mu kohereza ikibazo", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const val = field === "ikibazo"
      ? e.target.value.slice(0, MAX_CHARS)
      : e.target.value;
    setForm(p => ({ ...p, [field]: val }));
    if (errors[field]) setErrors(p => { const n = { ...p }; delete n[field]; return n; });
  };

  const charCount  = form.ikibazo.length;
  const charLeft   = MAX_CHARS - charCount;
  const charColor  = charLeft < 100 ? "#ef4444" : charLeft < 300 ? "#f59e0b" : "#94a3b8";

  /* ── shared input style factory ── */
  const inputBase = (hasError) => ({
    width: "100%",
    padding: "13px 16px",
    border: `2px solid ${hasError ? "#f87171" : "rgba(255,255,255,0.15)"}`,
    borderRadius: 12,
    fontSize: 15,
    fontFamily: "inherit",
    outline: "none",
    background: hasError ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.07)",
    color: "#f1f5f9",
    caretColor: "#a78bfa",
    transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
    boxSizing: "border-box",
  });

  return (
    <div style={{ fontFamily: "'Nunito Sans', system-ui, sans-serif", maxWidth: 700, margin: "0 auto", padding: "0 4px" }}>

      {/* ── global style overrides ── */}
      <style>{`
        .oik-input::placeholder { color: rgba(148,163,184,0.7) !important; }
        .oik-input:focus {
          border-color: #a78bfa !important;
          box-shadow: 0 0 0 3px rgba(167,139,250,0.18) !important;
          background: rgba(167,139,250,0.06) !important;
        }
        .oik-input:hover:not(:focus) { border-color: rgba(255,255,255,0.28) !important; }
        .oik-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(124,58,237,0.45) !important;
        }
        .oik-submit:active:not(:disabled) { transform: translateY(0); }
        .oik-clear:hover { background: rgba(255,255,255,0.12) !important; color: #f1f5f9 !important; }
        .oik-clear:active { transform: scale(0.97); }
        @media (max-width: 480px) {
          .oik-btn-row { flex-direction: column !important; }
          .oik-btn-row button { width: 100% !important; }
        }
      `}</style>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, left: 20, zIndex: 9999,
          maxWidth: 420, margin: "0 auto",
          padding: "14px 20px", borderRadius: 14, fontWeight: 600, fontSize: 14,
          background: toast.type === "success"
            ? "linear-gradient(135deg,rgba(16,185,129,0.95),rgba(5,150,105,0.95))"
            : "linear-gradient(135deg,rgba(239,68,68,0.95),rgba(185,28,28,0.95))",
          color: "#fff",
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          backdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className={`bi bi-${toast.type === "success" ? "check-circle-fill" : "exclamation-triangle-fill"}`}
            style={{ fontSize: 18, flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{toast.msg}</span>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: "0 0 6px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            width: 40, height: 40, borderRadius: 12,
            background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, boxShadow: "0 4px 14px rgba(124,58,237,0.35)",
          }}>
            <i className="bi bi-chat-square-text-fill" style={{ color: "#fff", fontSize: 16 }} />
          </span>
          Ohereza Ikibazo Ku Kagari
        </h2>
        <p style={{ color: "#94a3b8", fontSize: 13.5, margin: 0, paddingLeft: 50, lineHeight: 1.6 }}>
          Andika ikibazo cyawe hano maze ukohereze Akagari
        </p>
      </div>

      {/* ── Info banner ── */}
      <div style={{
        padding: "13px 16px",
        background: "rgba(124,58,237,0.1)",
        border: "1px solid rgba(124,58,237,0.25)",
        borderRadius: 12, marginBottom: 22,
        display: "flex", gap: 10, alignItems: "flex-start",
      }}>
        <i className="bi bi-info-circle-fill" style={{ color: "#a78bfa", fontSize: 16, flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13.5, color: "#c4b5fd", lineHeight: 1.65 }}>
          Ikibazo cyawe kizagera kuri Akagari vuba. Uzabona igisubizo mu gice cya{" "}
          <strong style={{ color: "#e9d5ff" }}>"Ibibazo Byanjye"</strong>.
        </div>
      </div>

      {/* ── Form card ── */}
      <div style={{
        background: "linear-gradient(145deg,rgba(15,23,42,0.85),rgba(10,15,30,0.92))",
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.1)",
        padding: "28px 24px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        backdropFilter: "blur(16px)",
      }}>
        <form onSubmit={handleSubmit} noValidate>

          {/* Title field */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#cbd5e1", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <i className="bi bi-type" style={{ color: "#a78bfa", marginRight: 5 }} />
              Umutwe w'Ikibazo <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              className="oik-input"
              value={form.title}
              onChange={handleChange("title")}
              placeholder="Injiza umutwe w'ikibazo..."
              maxLength={255}
              style={inputBase(!!errors.title)}
            />
            {errors.title && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#f87171", display: "flex", alignItems: "center", gap: 5 }}>
                <i className="bi bi-exclamation-circle-fill" style={{ fontSize: 11 }} />
                {errors.title}
              </div>
            )}
          </div>

          {/* Ikibazo textarea */}
          <div style={{ marginBottom: 26 }}>
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#cbd5e1", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <i className="bi bi-chat-text" style={{ color: "#a78bfa", marginRight: 5 }} />
              Ikibazo <span style={{ color: "#f87171" }}>*</span>
            </label>
            <textarea
              ref={textareaRef}
              className="oik-input"
              value={form.ikibazo}
              onChange={handleChange("ikibazo")}
              placeholder="Sobanura ikibazo cyawe mu buryo burambuye..."
              rows={4}
              style={{
                ...inputBase(!!errors.ikibazo),
                resize: "none",
                minHeight: 120,
                lineHeight: 1.75,
                overflow: "hidden",
                display: "block",
              }}
            />
            {/* counter + error row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 7, gap: 8 }}>
              {errors.ikibazo ? (
                <div style={{ fontSize: 12, color: "#f87171", display: "flex", alignItems: "center", gap: 5 }}>
                  <i className="bi bi-exclamation-circle-fill" style={{ fontSize: 11 }} />
                  {errors.ikibazo}
                </div>
              ) : <span />}
              <span style={{
                fontSize: 12, fontWeight: 600, color: charColor,
                background: "rgba(255,255,255,0.05)",
                padding: "3px 10px", borderRadius: 20,
                border: `1px solid ${charColor}40`,
                flexShrink: 0, transition: "color 0.3s",
              }}>
                {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} inyuguti
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div className="oik-btn-row" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={loading}
              className="oik-submit"
              style={{
                flex: 1, minWidth: 160, padding: "13px 24px",
                background: loading
                  ? "rgba(124,58,237,0.35)"
                  : "linear-gradient(135deg,#7c3aed,#6d28d9)",
                color: "#fff", border: "none", borderRadius: 12,
                fontWeight: 700, fontSize: 14.5,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: loading ? "none" : "0 4px 18px rgba(124,58,237,0.35)",
                transition: "all 0.2s",
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)",
                    borderTopColor: "#fff", borderRadius: "50%",
                    animation: "oik-spin 0.7s linear infinite", display: "inline-block",
                  }} />
                  Kohereza...
                </>
              ) : (
                <><i className="bi bi-send-fill" />Ohereza Ikibazo</>
              )}
            </button>
            <button
              type="button"
              className="oik-clear"
              onClick={() => { setForm({ title: "", ikibazo: "" }); setErrors({}); }}
              style={{
                padding: "13px 22px",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 12, fontWeight: 600, fontSize: 14,
                cursor: "pointer", color: "#94a3b8",
                display: "flex", alignItems: "center", gap: 7,
                transition: "all 0.2s",
              }}
            >
              <i className="bi bi-x-lg" />Siba
            </button>
          </div>
        </form>
      </div>

      {/* ── Tips ── */}
      <div style={{
        marginTop: 18, padding: "14px 18px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
      }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <i className="bi bi-lightbulb-fill" style={{ color: "#f59e0b" }} />Inama
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#94a3b8", lineHeight: 1.85 }}>
          <li>Sobanura ikibazo cyawe mu buryo burambuye</li>
          <li>Shyiramo amakuru yose akenewe kugira Akagari akusubize vuba</li>
          <li>Uzabona igisubizo mu gice cya "Ibibazo Byanjye"</li>
        </ul>
      </div>

      {/* spinner keyframe */}
      <style>{`@keyframes oik-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default OherezaIkibazo;
